// src/pages/FillBlankedit.jsx
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Fillblank.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

const apiBase = 'https://digital.tifleducation.com/admin/public/api';
const LOCAL_SELECTED_KEY = 'selectedQuestionsByChapter'; // chapter -> { typeKey: [ids] }

const isFillTypeLabel = (label) => {
  if (!label) return false;
  const s = String(label).toLowerCase();
  return s.includes('fill') && s.includes('blank');
};

const FillBlankedit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paperId } = useParams(); // get paperId from URL
  const state = location.state || {};

  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0,
    totalGrade: 0,
    totalSubjects: 0,
    totalPapers: 0,
    totalQuestions: 0,
  });

  const [user, setUser] = useState({
    id: null,
    name: '',
    email: '',
    password: '',
  });

  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [fillLimit, setFillLimit] = useState(0);

  // Get board/grade/subject from state or fallback to localStorage
  const boardId = state.boardId || localStorage.getItem('selectedBoardId');
  const gradeId = state.gradeId || localStorage.getItem('selectedGradeId');
  const subjectId = state.subjectId || localStorage.getItem('selectedSubjectId');
  const initialFillLimit = state.fillLimit || Number(JSON.parse(localStorage.getItem("regularPaperForm") || "{}").fill || 0);

  // Toggle dropdown
  const toggleDropdown = () => setShowDropdown(prev => !prev);

  // Fetch dashboard & user data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${apiBase}/getDashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });

          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || '',
              email: res.data.user.email || '',
              password: '',
            });
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };

    fetchDashboardData();
  }, []);

  // Fetch Fill in the Blanks Questions for editing
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!boardId || !gradeId || !subjectId) {
        toast.error("Missing selected board/grade/subject. Please go back and select.");
        return;
      }

      setFillLimit(initialFillLimit);

      try {
        const token = localStorage.getItem('token');
        let data = {};

        if (paperId) {
          // Fetch existing paper questions
          const res = await axios.get(`${apiBase}/papers/${paperId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          data = res.data?.questions || {};
        } else {
          // Fetch new questions
          const res = await axios.post(
            `${apiBase}/papers/fetch-questions`,
            { board: Number(boardId), grade: Number(gradeId), subject: Number(subjectId) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          data = res.data || {};
        }

        // Flatten all Fill-in-the-Blank questions
        const fillQs = [];
        Object.entries(data).forEach(([chapterId, chapterData]) => {
          const types = chapterData.types || {};
          Object.entries(types).forEach(([typeLabel, qArray]) => {
            if (isFillTypeLabel(typeLabel)) {
              (Array.isArray(qArray) ? qArray : []).forEach(q => {
                const chapter_id = q.chapter_id ?? Number(chapterId);
                fillQs.push({ ...q, chapter_id });
              });
            }
          });
        });

        setQuestions(fillQs);

        // restore selected questions map
        const savedByChapter = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
        const selectedMap = {};
        Object.entries(savedByChapter || {}).forEach(([ch, types]) => {
          const arr = types?.fill_in_the_blanks || [];
          (Array.isArray(arr) ? arr : []).forEach(id => {
            selectedMap[Number(id)] = true;
          });
        });
        setSelectedQuestions(selectedMap);

      } catch (error) {
        console.error("Error fetching fill in the blanks questions:", error.response?.data || error);
        toast.error("Failed to fetch questions.");
      }
    };

    fetchQuestions();
  }, [boardId, gradeId, subjectId, paperId]);

  // Handle selecting a question checkbox
  const handleSelectQuestion = async (questionId, chapterId) => {
    const alreadySelected = !!selectedQuestions[questionId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!fillLimit) {
      toast.error("No Fill in the blanks count set. Please go back and set it first.");
      return;
    }

    if (!alreadySelected && currentCount >= fillLimit) {
      toast.error(`You can only select up to ${fillLimit} Fill in the blanks questions.`);
      return;
    }

    setSelectedQuestions(prev => {
      const next = { ...prev };
      if (next[questionId]) delete next[questionId];
      else next[questionId] = true;
      return next;
    });

    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
      const chapterKey = String(chapterId);
      if (!saved[chapterKey]) saved[chapterKey] = {};
      if (!Array.isArray(saved[chapterKey].fill_in_the_blanks)) saved[chapterKey].fill_in_the_blanks = [];

      const arr = saved[chapterKey].fill_in_the_blanks;
      const idx = arr.findIndex(x => Number(x) === Number(questionId));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(Number(questionId));

      saved[chapterKey].fill_in_the_blanks = arr;
      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      if (paperId) {
        const token = localStorage.getItem('token');
        await axios.put(
          `${apiBase}/papers/${paperId}`,
          { selectedQuestions: saved },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('Selection saved');
    } catch (error) {
      console.error('Error saving selected questions:', error.response?.data || error);
      toast.error('Failed to save selection');
    }
  };

  // Handle profile save
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${apiBase}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        <div className="fill-header">
          <div className="fill-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="fill-welcome-text">Fill In The Blanks</span>
          </div>

          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className='edituser' onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="fill-questions-container">
          {questions.length > 0 ? (
            questions.map((q, index) => (
              <div key={q.id} className="fill-question-box">
                <div className="fill-question">
                  <input
                    type="checkbox"
                    className="fill-checkbox"
                    checked={!!selectedQuestions[q.id]}
                    onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                  />
                  <span
                    className="question-text"
                    dangerouslySetInnerHTML={{
                      __html: `${index + 1}. ${String(q.question_title || q.question || q.question_text || '').replace(/<\/?p>/g, '')}`
                    }}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No Fill in the Blanks Questions Found</h3>
              <p style={{ color: '#666' }}>There are currently no fill-in-the-blanks questions for this Board / Grade / Subject.</p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>

            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input
                type="password"
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0, left: 0, width: "100%", height: "100%",
    background: "rgba(0,0,0,0.6)",
    display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000
  },
  modal: {
    background: "#fff", padding: "2rem", borderRadius: "12px",
    width: "90%", maxWidth: "400px", position: "relative",
    boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
  },
  closeBtn: {
    position: "absolute", top: "10px", right: "15px",
    background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer"
  },
  userIcon: {
    textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem"
  },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default FillBlankedit;
